/**
 * NMS Product Navigation Pills
 * Handles smooth scrolling to sections and active state management
 */

class NMSProductNavigation {
  constructor(element) {
    this.element = element;
    this.navPills = this.element.querySelectorAll('[data-nms-js="nav-pill"]');
    this.sections = new Map();
    this.selectorCache = new Map(); // Cache successful selectors for performance
    this.activePill = null;
    this.isScrolling = false;
    this.scrollOffset = 120;
    this.isNavigatingByArrow = false;
    this.scrollDetectionRAF = null;
    this.lastManualUpdate = 0;
    this.updateDebounceTime = 800;

    // Store bound references for proper cleanup
    this._boundHandlePillClick = this.handlePillClick.bind(this);
    this._boundHandleKeydown = this.handleKeydown.bind(this);
    this._boundHandleLeftArrowClick = this.handleLeftArrowClick.bind(this);
    this._boundHandleRightArrowClick = this.handleRightArrowClick.bind(this);
    this._boundUpdateScrollGradients = this.updateScrollGradients.bind(this);
    this._boundUpdateArrowVisibility = this.updateArrowVisibility.bind(this);

    this.init();
  }

  init() {
    this.bindEvents();
    this.mapSections();
    this.hideInvalidPills();
    this.observeSections();
    this.setupStickyFallback();
    this.setupScrollGradients();
    this.setupMobileArrows();

    // Detect initial active section after initialization
    // Use RAF + timeout to ensure DOM is measured correctly
    // Force detection to bypass manual update debounce check
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.detectActiveSection(true);
      }, 100);
    });
  }

  bindEvents() {
    this.navPills.forEach(pill => {
      pill.addEventListener('click', this._boundHandlePillClick);
      pill.addEventListener('keydown', this._boundHandleKeydown);
    });

    this._boundThrottledHandleScroll = this.throttle(this.handleScroll.bind(this), 100);
    window.addEventListener('scroll', this._boundThrottledHandleScroll);
  }

  // Helper method to get fresh element from stored selectors
  getSectionElement(pill) {
    const selectors = this.sections.get(pill);
    if (!selectors) return null;

    // Performance optimization: Try cached selector first
    const cachedSelector = this.selectorCache.get(pill);
    if (cachedSelector) {
      const element = document.querySelector(cachedSelector);
      if (element) return element;

      // Cache is stale (DOM changed), clear it and try all selectors
      this.selectorCache.delete(pill);
    }

    // Try all selectors and cache the successful one
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        this.selectorCache.set(pill, selector);
        return element;
      }
    }

    return null;
  }

  mapSections() {
    // Store SELECTORS instead of element references to avoid stale references after DOM updates
    this.navPills.forEach(pill => {
      const targetSelector = pill.dataset.target;

      // Build array of possible selectors to try
      const selectors = [
        `.${targetSelector}`,
        `#${targetSelector}`,
        `[class*="${targetSelector}"]`,
        `section[class*="${targetSelector.replace('nms-', '')}"]`
      ];

      // Test if we can find the element now
      let targetElement = null;
      for (const selector of selectors) {
        targetElement = document.querySelector(selector);
        if (targetElement) break;
      }

      if (targetElement) {
        // Store the selector, not the element reference
        this.sections.set(pill, selectors);
      }
    });
  }

  hideInvalidPills() {
    // Hide pills that don't have valid target sections
    this.navPills.forEach(pill => {
      if (!this.sections.has(pill)) {
        const pillItem = pill.closest('.nms-product-navigation__item');
        if (pillItem) {
          pillItem.style.display = 'none';
          // Also mark it as hidden for potential future reference
          pillItem.setAttribute('data-hidden', 'true');
        }
      }
    });

    // Update navPills to only include visible pills
    this.navPills = Array.from(this.navPills).filter(pill => this.sections.has(pill));
  }

  handlePillClick(event) {
    event.preventDefault();
    const pill = event.currentTarget;
    const targetSection = this.getSectionElement(pill);

    if (targetSection) {
      this.scrollToSection(targetSection, pill);
    } else {
      const pillText = pill.textContent.trim().toLowerCase();
      const fallbackSelectors = [
        `.nms-product-${pillText}`,
        `.section-nms-product-${pillText}`,
        `[id*="${pillText}"]`,
        `[class*="${pillText}"]`
      ];

      for (const selector of fallbackSelectors) {
        const fallbackElement = document.querySelector(selector);
        if (fallbackElement) {
          this.scrollToSection(fallbackElement, pill);
          return;
        }
      }
    }
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handlePillClick(event);
    }
  }

  scrollToSection(targetSection, pill) {
    this.isScrolling = true;
    this.updateActiveState(pill, true); // true = manual update

    const elementPosition = targetSection.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - this.scrollOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    // Extended timeout for smooth scrolling completion
    setTimeout(() => {
      this.isScrolling = false;
    }, 1500);
  }

  updateActiveState(activePill, isManual = false) {
    // If this is a manual update (click/arrow), record timestamp
    if (isManual) {
      this.lastManualUpdate = Date.now();
    }

    // Remove active class from all pills immediately
    this.navPills.forEach(pill => {
      pill.classList.remove('nms-product-navigation__pill--active');
      pill.setAttribute('aria-current', 'false');
    });

    if (activePill) {
      // Add active class immediately (sync operation)
      activePill.classList.add('nms-product-navigation__pill--active');
      activePill.setAttribute('aria-current', 'page');
      this.activePill = activePill;

      // Auto-scroll active pill into view on mobile
      this.scrollPillIntoView(activePill);

      // Update arrow visibility
      this.updateArrowVisibility();
    } else {
      // No active pill - user scrolled past all sections
      this.activePill = null;

      // Update arrow visibility
      this.updateArrowVisibility();
    }
  }

  scrollPillIntoView(activePill) {
    if (!this.navContainer || !activePill) return;

    // Only auto-scroll on smaller screens where navigation is horizontally scrollable
    if (window.innerWidth >= 1024) return;

    const navRect = this.navContainer.getBoundingClientRect();
    const pillRect = activePill.getBoundingClientRect();

    // Calculate if pill is outside the visible area
    const pillLeft = pillRect.left - navRect.left;
    const pillRight = pillLeft + pillRect.width;
    const containerWidth = navRect.width;

    // Add some padding so pill isn't right at the edge
    const padding = 20;

    let scrollLeft = this.navContainer.scrollLeft;

    // If pill is cut off on the left, scroll left
    if (pillLeft < padding) {
      scrollLeft += pillLeft - padding;
    }
    // If pill is cut off on the right, scroll right
    else if (pillRight > containerWidth - padding) {
      scrollLeft += pillRight - containerWidth + padding;
    }

    // Smooth scroll to position
    if (scrollLeft !== this.navContainer.scrollLeft) {
      this.navContainer.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }

  handleScroll() {
    if (this.isScrolling) return;

    const viewportHeight = window.innerHeight;
    let bestVisibility = 0;
    let correspondingPill = null;

    this.sections.forEach((selectors, pill) => {
      // Query fresh element
      const section = this.getSectionElement(pill);
      if (!section) return;

      const rect = section.getBoundingClientRect();
      const sectionHeight = rect.height;

      // Calculate how much of the section is visible
      const visibleTop = Math.max(0, -rect.top);
      const visibleBottom = Math.min(sectionHeight, viewportHeight - rect.top);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibilityRatio = visibleHeight / sectionHeight;

      // Only consider sections that are significantly visible (50%)
      if (visibilityRatio >= 0.5 && visibilityRatio > bestVisibility) {
        bestVisibility = visibilityRatio;
        correspondingPill = pill;
      }
    });

    if (correspondingPill && correspondingPill !== this.activePill) {
      this.updateActiveState(correspondingPill);
    }
  }

  observeSections() {
    // Use RAF-based scroll detection for smooth performance
    this.handleScrollDetection = () => {
      if (this.scrollDetectionRAF) return;

      this.scrollDetectionRAF = requestAnimationFrame(() => {
        if (!this.isScrolling && !this.isNavigatingByArrow) {
          this.detectActiveSection();
        }
        this.scrollDetectionRAF = null;
      });
    };

    window.addEventListener('scroll', this.handleScrollDetection, { passive: true });
  }

  detectActiveSection(forceDetection = false) {
    // Don't interfere with manual navigation for a period after click/arrow
    // Unless force detection is enabled (e.g., after re-initialization)
    if (!forceDetection) {
      const timeSinceManualUpdate = Date.now() - this.lastManualUpdate;
      if (timeSinceManualUpdate < this.updateDebounceTime) {
        return;
      }
    }

    if (this.sections.size === 0) {
      console.error('NMS Nav: No sections mapped! Cannot detect active section.');
      return;
    }

    let bestPill = null;
    let bestScore = -1;
    let lastSectionElement = null;
    let lastSectionPill = null;

    this.sections.forEach((selectors, pill) => {
      // Query fresh element using stored selectors to avoid stale references
      let section = null;
      for (const selector of selectors) {
        section = document.querySelector(selector);
        if (section) break;
      }

      // If element not found, skip this pill
      if (!section) {
        return;
      }

      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const sectionHeight = rect.height;

      // Track the last section (bottommost) with a pill
      if (!lastSectionElement || section.getBoundingClientRect().top > lastSectionElement.getBoundingClientRect().top) {
        lastSectionElement = section;
        lastSectionPill = pill;
      }

      // Skip if section is not in viewport at all
      if (rect.bottom <= this.scrollOffset || rect.top >= viewportHeight) {
        return;
      }

      // Calculate actual visible area
      const visibleTop = Math.max(this.scrollOffset, rect.top);
      const visibleBottom = Math.min(viewportHeight, rect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibilityRatio = sectionHeight > 0 ? visibleHeight / sectionHeight : 0;

      // Simple scoring based on visibility and position
      let score = visibilityRatio * 100;

      // Boost for sections that are well positioned in viewport
      if (rect.top >= this.scrollOffset && rect.top <= viewportHeight * 0.4) {
        score += 50;
      }

      // Boost for sections that are mostly visible
      if (visibilityRatio > 0.6) {
        score += 30;
      }

      // Special boost for small sections that are fully visible
      if (sectionHeight < viewportHeight * 0.5 && visibilityRatio > 0.8) {
        score += 40;
      }

      if (score > bestScore) {
        bestScore = score;
        bestPill = pill;
      }
    });

    // Check if user has scrolled past the last section with a pill
    if (lastSectionElement) {
      const lastSectionRect = lastSectionElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Only deactivate if the section is completely out of view above the viewport
      // This means the bottom of the section is above the top of the viewport (negative bottom position)
      const scrolledPastLastSection = lastSectionRect.bottom < 0;

      // If scrolled past last section, deactivate all pills
      if (scrolledPastLastSection && this.activePill !== null) {
        this.updateActiveState(null, false); // null = deactivate all
        return;
      }
    }

    // Only update if we have a clear winner and it's different from current
    if (bestPill && bestPill !== this.activePill && bestScore > 60) {
      this.updateActiveState(bestPill, false); // false = not manual
    }
  }

  findPillBySection(targetSection) {
    for (const [pill, section] of this.sections) {
      if (section === targetSection) {
        return pill;
      }
    }
    return null;
  }

  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  setupStickyFallback() {
    // Use Intersection Observer for smoother detection
    this.setupStickyObserver();
  }

  setupStickyObserver() {
    // Get the original position before any sticky behavior
    this.originalTop = this.element.getBoundingClientRect().top + window.pageYOffset;
    this.isSticky = false;
    this.rafId = null;

    // Use RAF-based scroll detection for buttery smooth performance
    this.handleStickyScroll = () => {
      if (this.rafId) return; // Prevent multiple RAF calls

      this.rafId = requestAnimationFrame(() => {
        const scrollY = window.pageYOffset;
        const shouldBeSticky = scrollY >= (this.originalTop - this.scrollOffset);

        if (shouldBeSticky !== this.isSticky) {
          this.updateStickyState(shouldBeSticky);
          this.isSticky = shouldBeSticky;
        }

        this.rafId = null;
      });
    };

    // Use passive listener for better performance
    window.addEventListener('scroll', this.handleStickyScroll, { passive: true });

    // Initial check
    this.handleStickyScroll();
  }

  updateStickyState(shouldBeSticky) {
    // Direct class manipulation without additional RAF since we're already in one
    if (shouldBeSticky) {
      this.element.classList.add('nms-product-navigation--sticky');
    } else {
      this.element.classList.remove('nms-product-navigation--sticky');
    }
  }

  setupScrollGradients() {
    this.navContainer = this.element.querySelector('.nms-product-navigation__nav');
    this.leftGradient = this.element.querySelector('[data-nms-js="gradient-left"]');
    this.rightGradient = this.element.querySelector('[data-nms-js="gradient-right"]');

    if (!this.navContainer || !this.leftGradient || !this.rightGradient) return;

    this.updateScrollGradients();
    this.navContainer.addEventListener('scroll', this._boundUpdateScrollGradients);
    window.addEventListener('resize', this._boundUpdateScrollGradients);
  }

  setupMobileArrows() {
    this.leftArrow = this.element.querySelector('[data-nms-js="nav-arrow-left"]');
    this.rightArrow = this.element.querySelector('[data-nms-js="nav-arrow-right"]');
    this.currentPillIndex = 0;

    if (!this.leftArrow || !this.rightArrow) return;

    // Bind arrow click events
    this.leftArrow.addEventListener('click', this._boundHandleLeftArrowClick);
    this.rightArrow.addEventListener('click', this._boundHandleRightArrowClick);

    // Update arrow visibility
    this.updateArrowVisibility();

    // Update on resize
    window.addEventListener('resize', this._boundUpdateArrowVisibility);

    // Update on scroll (for arrow visibility based on nav scroll position)
    if (this.navContainer) {
      this.navContainer.addEventListener('scroll', this._boundUpdateArrowVisibility);
    }
  }

  handleLeftArrowClick() {
    if (this.currentPillIndex > 0) {
      this.currentPillIndex--;
      this.navigateToTabByIndex(this.currentPillIndex);
    }
  }

  handleRightArrowClick() {
    if (this.currentPillIndex < this.navPills.length - 1) {
      this.currentPillIndex++;
      this.navigateToTabByIndex(this.currentPillIndex);
    }
  }

  navigateToTabByIndex(index) {
    const targetPill = this.navPills[index];
    if (!targetPill) return;

    // Set both navigation flags to completely prevent scroll detection conflicts
    this.isNavigatingByArrow = true;
    this.isScrolling = true;

    // Mark this as a manual update
    this.lastManualUpdate = Date.now();

    // Update internal state first
    this.activePill = targetPill;
    this.currentPillIndex = index;

    // Start scrolling to the section first
    const targetSection = this.getSectionElement(targetPill);
    if (targetSection) {
      const elementPosition = targetSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - this.scrollOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }

    // Apply active class with a small delay to let scrolling start
    setTimeout(() => {
      this.navPills.forEach(pill => {
        pill.classList.remove('nms-product-navigation__pill--active');
        pill.setAttribute('aria-current', 'false');
      });

      targetPill.classList.add('nms-product-navigation__pill--active');
      targetPill.setAttribute('aria-current', 'page');

      // Auto-scroll the pill into view
      this.scrollPillIntoView(targetPill);

      // Update arrow visibility
      this.updateArrowVisibility();
    }, 150);

    // Reset both navigation flags after smooth scrolling completes
    setTimeout(() => {
      this.isNavigatingByArrow = false;
      this.isScrolling = false;
    }, 2000);
  }

  updateArrowVisibility() {
    // Only show arrows on mobile
    if (window.innerWidth >= 1024) {
      this.leftArrow?.classList.remove('nms-product-navigation__arrow--visible');
      this.rightArrow?.classList.remove('nms-product-navigation__arrow--visible');
      return;
    }

    // Update current pill index based on active pill
    const activePill = this.element.querySelector('.nms-product-navigation__pill--active');
    if (activePill) {
      this.currentPillIndex = Array.from(this.navPills).indexOf(activePill);
    }

    // Show/hide left arrow and ensure left gradient is visible when arrow is shown
    if (this.currentPillIndex > 0) {
      this.leftArrow?.classList.add('nms-product-navigation__arrow--visible');
      // Force left gradient to be visible when left arrow is shown
      if (this.leftGradient) {
        this.leftGradient.classList.add('nms-product-navigation__gradient--visible');
      }
    } else {
      this.leftArrow?.classList.remove('nms-product-navigation__arrow--visible');
      // Hide left gradient when left arrow is hidden
      if (this.leftGradient) {
        this.leftGradient.classList.remove('nms-product-navigation__gradient--visible');
      }
    }

    // Show/hide right arrow and ensure right gradient is visible when arrow is shown
    if (this.currentPillIndex < this.navPills.length - 1) {
      this.rightArrow?.classList.add('nms-product-navigation__arrow--visible');
      // Force right gradient to be visible when right arrow is shown
      if (this.rightGradient) {
        this.rightGradient.classList.add('nms-product-navigation__gradient--visible');
      }
    } else {
      this.rightArrow?.classList.remove('nms-product-navigation__arrow--visible');
      // Hide right gradient when right arrow is hidden
      if (this.rightGradient) {
        this.rightGradient.classList.remove('nms-product-navigation__gradient--visible');
      }
    }
  }

  updateScrollGradients() {
    if (!this.navContainer || !this.leftGradient || !this.rightGradient) return;

    const scrollLeft = this.navContainer.scrollLeft;
    const scrollWidth = this.navContainer.scrollWidth;
    const clientWidth = this.navContainer.clientWidth;
    const maxScrollLeft = scrollWidth - clientWidth;

    // Show left gradient if scrolled from the beginning
    if (scrollLeft > 1) {
      this.leftGradient.classList.add('nms-product-navigation__gradient--visible');
    } else {
      this.leftGradient.classList.remove('nms-product-navigation__gradient--visible');
    }

    // Show right gradient if there's content to scroll
    // Only hide when truly at the end (within 1px tolerance)
    if (maxScrollLeft > 0 && scrollLeft < maxScrollLeft - 1) {
      this.rightGradient.classList.add('nms-product-navigation__gradient--visible');
    } else {
      this.rightGradient.classList.remove('nms-product-navigation__gradient--visible');
    }
  }


  destroy() {
    this.navPills.forEach(pill => {
      pill.removeEventListener('click', this._boundHandlePillClick);
      pill.removeEventListener('keydown', this._boundHandleKeydown);
    });

    if (this._boundThrottledHandleScroll) {
      window.removeEventListener('scroll', this._boundThrottledHandleScroll);
    }
    window.removeEventListener('resize', this._boundUpdateScrollGradients);
    window.removeEventListener('resize', this._boundUpdateArrowVisibility);

    if (this.navContainer) {
      this.navContainer.removeEventListener('scroll', this._boundUpdateScrollGradients);
      this.navContainer.removeEventListener('scroll', this._boundUpdateArrowVisibility);
    }

    // Clean up sticky scroll handler
    if (this.handleStickyScroll) {
      window.removeEventListener('scroll', this.handleStickyScroll);
    }

    // Clean up scroll detection handler
    if (this.handleScrollDetection) {
      window.removeEventListener('scroll', this.handleScrollDetection);
    }

    // Clean up any pending RAF
    if (this.scrollDetectionRAF) {
      cancelAnimationFrame(this.scrollDetectionRAF);
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    // Clean up arrow event listeners
    if (this.leftArrow) {
      this.leftArrow.removeEventListener('click', this._boundHandleLeftArrowClick);
    }
    if (this.rightArrow) {
      this.rightArrow.removeEventListener('click', this._boundHandleRightArrowClick);
    }

    // Clear the sections Map and selector cache to free memory
    this.sections.clear();
    this.selectorCache.clear();

    // Remove instance reference from element
    if (this.element) {
      delete this.element.nmsProductNavigation;
    }
  }
}

// Initialize when DOM is ready or immediately if already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeNavigation);
} else {
  initializeNavigation();
}

function initializeNavigation() {
  const navElements = document.querySelectorAll('[data-nms-js="product-navigation"]');

  navElements.forEach(element => {
    if (!element.nmsProductNavigation) {
      element.nmsProductNavigation = new NMSProductNavigation(element);
    }
  });
}

if (!window.neemans) {
  window.neemans = {};
}
window.neemans.ProductNavigation = NMSProductNavigation;

// Re-initialize navigation when color swatches update content
document.addEventListener('nms:color-content-updated', (event) => {
  // Navigation is in a separate section, not inside the updated container
  // Find ALL navigation elements on the page and re-map their sections
  const navElements = document.querySelectorAll('[data-nms-js="product-navigation"]');

  navElements.forEach(element => {
    // Don't destroy and recreate - just re-map sections and detect active
    if (element.nmsProductNavigation) {
      const instance = element.nmsProductNavigation;

      // Clear existing section mappings and selector cache
      instance.sections.clear();
      instance.selectorCache.clear();

      // Re-map sections to new DOM elements
      instance.mapSections();

      // Hide invalid pills
      instance.hideInvalidPills();

      // Force detection after a delay to ensure DOM is ready
      requestAnimationFrame(() => {
        setTimeout(() => {
          instance.detectActiveSection(true);
        }, 300);
      });
    }
  });
});
